import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Camera, Loader2, PackageCheck, ScanLine, Ship, TriangleAlert } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api/client'
import type {
  WarehouseParcel,
  WarehouseParcelStatus,
  WarehouseRateCard,
  WarehouseView,
} from '@/lib/api/types'
import { ErrorState } from '@/src/components/ErrorState'
import {
  ParcelTrackingPanel,
  ParcelTrackingSummary,
} from '@/src/components/tracking/ParcelTrackingPanel'
import { LoadingState } from '@/src/components/LoadingState'
import { HintedLabel } from '@/src/components/FieldHint'
import { FIELD_HELP } from '@/src/lib/onboarding/field-help'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { groupParcels } from '@/src/lib/parcels'

/**
 * The China warehouse: what customers said is coming, what has arrived, and
 * the parcels nobody has claimed. Built for a phone in the warehouse: find a
 * parcel by its tracking number or the code on its label, photograph and weigh
 * it, and mark it processed with the week it should fly.
 */

const VIEWS: Array<{ value: WarehouseView; label: string }> = [
  { value: 'expected', label: 'Expected' },
  { value: 'received', label: 'Received' },
  { value: 'unclaimed', label: 'Unclaimed' },
  { value: 'all', label: 'All' },
]

const STATUS: Record<WarehouseParcelStatus, { label: string; tone: string }> = {
  EXPECTED: { label: 'Expected', tone: 'tone-waiting' },
  RECEIVED: { label: 'Received', tone: 'tone-moving' },
  CONSOLIDATED: { label: 'On a consignment', tone: 'tone-done' },
  CANCELLED: { label: 'Cancelled', tone: 'tone-neutral' },
}

/** The coming weeks, by their Mondays as YYYY-MM-DD, for choosing when a parcel flies. */
export function upcomingMondays(count = 6, from = new Date()) {
  // From the week tomorrow falls in: on a Sunday, this week is already over.
  const monday = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate() + 1))
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7))
  return Array.from({ length: count }, (_, index) => {
    const day = new Date(monday)
    day.setUTCDate(monday.getUTCDate() + index * 7)
    return day.toISOString().slice(0, 10)
  })
}

function weekLabel(isoMonday: string) {
  const date = new Date(`${isoMonday}T00:00:00Z`)
  return `Week of ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`
}

function money(value: number | null | undefined, currency = 'USD') {
  return value == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)
}

function customerLabel(parcel: WarehouseParcel) {
  if (!parcel.customer) return 'Unclaimed'
  return `${parcel.customer.code ?? ''} · ${parcel.customer.company_name || parcel.customer.name}`.trim()
}

export function WarehousePage() {
  const [params, setParams] = useSearchParams()
  const [view, setView] = useState<WarehouseView>('expected')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState<WarehouseParcel | null>(null)
  const parcels = useAsyncData(() => apiClient.listWarehouseParcels(view, search), [view, search])
  const rates = useAsyncData(() => apiClient.getWarehouseRates(), [])
  const profile = useAsyncData(() => apiClient.getProfile(), [])
  const isAdmin = Boolean(profile.data?.is_admin || profile.data?.role === 'admin')

  // A notification links here with ?parcel=<id>.
  const linked = params.get('parcel')
  useEffect(() => {
    if (!linked) return
    apiClient
      .getWarehouseParcel(linked)
      .then(setOpen)
      .catch(() => undefined)
  }, [linked])

  const close = () => {
    setOpen(null)
    if (linked) {
      params.delete('parcel')
      setParams(params, { replace: true })
    }
    parcels.reload()
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Warehouse"
        description="Guangzhou: parcels customers are sending, what has arrived, and what flies when."
      />

      <ReceiveCard onFound={setOpen} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={view} onValueChange={(value) => setView(value as WarehouseView)}>
          <TabsList data-tour="warehouse-views">
            {VIEWS.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input
          className="sm:max-w-xs"
          placeholder="Search tracking, code, customer…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search parcels"
        />
      </div>

      {parcels.loading && !parcels.data ? (
        <LoadingState label="Loading parcels" />
      ) : parcels.error ? (
        <ErrorState error={parcels.error} onRetry={parcels.reload} />
      ) : !parcels.data?.length ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {view === 'expected'
              ? 'No parcels are on their way right now.'
              : view === 'unclaimed'
                ? 'Every parcel here has an owner.'
                : 'Nothing here yet.'}
          </CardContent>
        </Card>
      ) : (
        groupParcels(view, parcels.data).map((group) => (
          <section key={group.key} className="space-y-2" aria-labelledby={`group-${group.key}`}>
            {group.title && (
              <div>
                <h2 id={`group-${group.key}`} className="text-sm font-semibold">
                  {group.title}
                </h2>
                <p className="text-xs text-muted-foreground">{group.description}</p>
              </div>
            )}
            <ul className="grid gap-3" aria-label={group.title ?? 'Parcels'}>
              {group.parcels.map((parcel) => (
                <li key={parcel.id}>
                  <ParcelRow parcel={parcel} onOpen={() => setOpen(parcel)} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      <RatesCard
        rates={rates.data}
        loading={rates.loading}
        isAdmin={isAdmin}
        onChanged={rates.reload}
      />

      {open && <ParcelDialog parcel={open} onClose={close} onChanged={setOpen} />}
    </div>
  )
}

function ParcelRow({ parcel, onOpen }: { parcel: WarehouseParcel; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{customerLabel(parcel)}</span>
        <span className="flex items-center gap-1.5">
          {(parcel.dangerous_confirmed ?? parcel.declared_dangerous) && (
            <Badge variant="destructive">Dangerous goods</Badge>
          )}
          <Badge variant="outline" className={STATUS[parcel.status].tone}>
            {STATUS[parcel.status].label}
          </Badge>
        </span>
      </div>
      <p className="mt-1 text-sm">{parcel.description}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {[
          parcel.tracking_number ?? 'No tracking number yet',
          parcel.platform,
          parcel.ship_week ? weekLabel(parcel.ship_week) : null,
          parcel.weight_kg != null ? `${parcel.weight_kg} kg` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {parcel.status === 'EXPECTED' && parcel.tracking && (
        <div className="mt-2">
          <ParcelTrackingSummary tracking={parcel.tracking} />
        </div>
      )}
    </button>
  )
}

/** Scan or type what is on the label; Helm finds the parcel or registers a new one. */
function ReceiveCard({ onFound }: { onFound: (parcel: WarehouseParcel) => void }) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const text = value.trim()
    if (!text) return
    setBusy(true)
    setError(null)
    try {
      const isCode = /^XTL[-\s]?\d{3,}$/i.test(text)
      const parcel = await apiClient.registerParcelArrival(
        isCode ? { customer_code: text } : { tracking_number: text },
      )
      setValue('')
      onFound(parcel)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not find or register the parcel')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card data-tour="warehouse-receive">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScanLine className="h-5 w-5" aria-hidden />
          Receive a parcel
        </CardTitle>
        <CardDescription>
          Scan or type the tracking number, or the customer code on the label (XTL…). A parcel
          nobody announced is registered so it can be processed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
          <Input
            autoFocus
            inputMode="text"
            autoCapitalize="characters"
            placeholder="SF1234567890 or XTL1001"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-label="Tracking number or customer code"
          />
          <Button type="submit" disabled={busy || !value.trim()}>
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PackageCheck className="h-4 w-4" />
            )}
            Find or receive
          </Button>
        </form>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}

function PhotoThumb({ parcelId, photoId }: { parcelId: string; photoId: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let current: string | null = null
    apiClient
      .parcelPhotoUrl(parcelId, photoId)
      .then((objectUrl) => {
        current = objectUrl
        setUrl(objectUrl)
      })
      .catch(() => undefined)
    return () => {
      if (current) URL.revokeObjectURL(current)
    }
  }, [parcelId, photoId])
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Parcel" className="h-20 w-20 rounded-md border object-cover" />
    </a>
  ) : (
    <div
      className="h-20 w-20 animate-pulse rounded-md border bg-muted"
      aria-label="Loading photo"
    />
  )
}

function ParcelDialog({
  parcel,
  onClose,
  onChanged,
}: {
  parcel: WarehouseParcel
  onClose: () => void
  onChanged: (parcel: WarehouseParcel) => void
}) {
  const weeks = upcomingMondays()
  const fileInput = useRef<HTMLInputElement>(null)
  const [weight, setWeight] = useState(parcel.weight_kg?.toString() ?? '')
  const [length, setLength] = useState(parcel.dimensions_cm?.length.toString() ?? '')
  const [width, setWidth] = useState(parcel.dimensions_cm?.width.toString() ?? '')
  const [height, setHeight] = useState(parcel.dimensions_cm?.height.toString() ?? '')
  const [dangerous, setDangerous] = useState(
    parcel.dangerous_confirmed ?? parcel.declared_dangerous,
  )
  const [shipWeek, setShipWeek] = useState(parcel.ship_week ?? weeks[0]!)
  const [notes, setNotes] = useState(parcel.warehouse_notes ?? '')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState<'photo' | 'process' | 'claim' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const editable = parcel.status === 'EXPECTED' || parcel.status === 'RECEIVED'
  const weightKg = Number(weight)
  const canProcess = editable && parcel.photo_ids.length > 0 && weightKg > 0 && Boolean(shipWeek)
  const number = (text: string) => (text.trim() ? Number(text) : null)
  // Airlines' volume weight: cm³ ÷ 6000, as Helm charges the greater of the two.
  const sides = [length, width, height].map(number)
  const volumeKg = sides.every((side) => side != null && side > 0)
    ? Math.round(((sides[0]! * sides[1]! * sides[2]!) / 6000) * 100) / 100
    : null

  const refresh = async () => onChanged(await apiClient.getWarehouseParcel(parcel.id))

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setBusy('photo')
    setError(null)
    try {
      for (const file of Array.from(files)) await apiClient.uploadParcelPhoto(parcel.id, file)
      await refresh()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The photo could not be saved')
    } finally {
      setBusy(null)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const claim = async () => {
    setBusy('claim')
    setError(null)
    try {
      onChanged(await apiClient.claimParcel(parcel.id, code.trim()))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not claim the parcel')
    } finally {
      setBusy(null)
    }
  }

  const process = async (event: FormEvent) => {
    event.preventDefault()
    setBusy('process')
    setError(null)
    try {
      onChanged(
        await apiClient.processParcel(parcel.id, {
          weight_kg: weightKg,
          length_cm: number(length),
          width_cm: number(width),
          height_cm: number(height),
          dangerous,
          ship_week: shipWeek,
          notes: notes.trim() || null,
        }),
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not process the parcel')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto sm:max-w-lg"
        data-tour="warehouse-parcel"
      >
        <DialogHeader>
          <DialogTitle>{customerLabel(parcel)}</DialogTitle>
          <DialogDescription>
            {parcel.description} · {parcel.tracking_number ?? 'no tracking number'}
            {parcel.platform ? ` · ${parcel.platform}` : ''}
            {parcel.order_number ? ` · order ${parcel.order_number}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={STATUS[parcel.status].tone}>
              {STATUS[parcel.status].label}
            </Badge>
            {parcel.declared_dangerous && (
              <Badge variant="destructive">Declared dangerous goods</Badge>
            )}
            {parcel.price != null && (
              <span className="font-medium">
                {money(parcel.price)} · {parcel.chargeable_kg} kg charged
              </span>
            )}
          </div>

          {parcel.tracking && (
            <ParcelTrackingPanel
              tracking={parcel.tracking}
              title={parcel.description}
              initiallyOpen={parcel.status === 'EXPECTED'}
              onRefresh={async () => {
                try {
                  onChanged(await apiClient.refreshParcelTracking(parcel.id))
                  return null
                } catch (cause) {
                  return cause instanceof Error
                    ? cause.message
                    : 'Could not reach the tracking service'
                }
              }}
            />
          )}

          {(parcel.linked_consignments ?? []).map((consignment) => (
            <div key={consignment.id} className="space-y-2 rounded-md border p-3">
              <p className="flex items-start gap-2">
                <Ship className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  On consignment{' '}
                  <Link
                    to={`/consignments/${consignment.id}/tracking`}
                    className="font-medium underline underline-offset-2"
                  >
                    {consignment.code}
                  </Link>{' '}
                  · {consignment.title}
                  {consignment.customer
                    ? ` · ${[consignment.customer.code, consignment.customer.name].filter(Boolean).join(' · ')}`
                    : ''}
                </span>
              </p>
              {!parcel.customer && consignment.customer?.code && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy !== null}
                  onClick={async () => {
                    setBusy('claim')
                    setError(null)
                    try {
                      onChanged(await apiClient.claimParcel(parcel.id, consignment.customer!.code!))
                    } catch (cause) {
                      setError(
                        cause instanceof Error ? cause.message : 'Could not claim the parcel',
                      )
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  Give it to {consignment.customer.code}
                </Button>
              )}
            </div>
          ))}

          {!parcel.customer && (
            <div className="space-y-2 rounded-md border border-dashed p-3">
              <HintedLabel
                plain
                htmlFor="claim-code"
                hint={FIELD_HELP.claimCode}
                name="Customer code"
                wrap
              >
                Whose is it? Customer code from the label
              </HintedLabel>
              <div className="flex gap-2">
                <Input
                  id="claim-code"
                  placeholder="XTL1001"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
                <Button type="button" onClick={claim} disabled={busy !== null || !code.trim()}>
                  {busy === 'claim' && <Loader2 className="h-4 w-4 animate-spin" />}
                  Claim
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2" data-tour="warehouse-photos">
            <p className="font-medium">Photos</p>
            <div className="flex flex-wrap gap-2">
              {parcel.photo_ids.map((photoId) => (
                <PhotoThumb key={photoId} parcelId={parcel.id} photoId={photoId} />
              ))}
              {editable && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-20 w-20 flex-col gap-1"
                  onClick={() => fileInput.current?.click()}
                  disabled={busy !== null}
                >
                  {busy === 'photo' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                  <span className="text-xs">Add</span>
                </Button>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              aria-label="Add a photo of the parcel"
              onChange={(event) => upload(event.target.files)}
            />
            {editable && !parcel.photo_ids.length && (
              <p className="text-xs text-muted-foreground">
                Take at least one photo before marking it processed.
              </p>
            )}
          </div>

          {editable ? (
            <form id="process-parcel" onSubmit={process} className="space-y-4">
              <div className="grid grid-cols-2 gap-3" data-tour="warehouse-measure">
                <div className="col-span-2 space-y-1.5">
                  <HintedLabel plain htmlFor="weight" hint={FIELD_HELP.parcelWeight} name="Weight">
                    Weight (kg)
                  </HintedLabel>
                  <Input
                    id="weight"
                    inputMode="decimal"
                    value={weight}
                    onChange={(event) => setWeight(event.target.value)}
                    placeholder="e.g. 2.4"
                  />
                </div>
                {(
                  [
                    ['length', 'Length', length, setLength],
                    ['width', 'Width', width, setWidth],
                    ['height', 'Height', height, setHeight],
                  ] as const
                ).map(([id, label, value, set]) => (
                  <div key={id} className="space-y-1.5">
                    {id === 'length' ? (
                      <HintedLabel
                        plain
                        htmlFor={id}
                        hint={FIELD_HELP.parcelDimensions}
                        name="Length, width and height"
                      >
                        {label} (cm)
                      </HintedLabel>
                    ) : (
                      <Label htmlFor={id}>{label} (cm)</Label>
                    )}
                    <Input
                      id={id}
                      inputMode="decimal"
                      value={value}
                      onChange={(event) => set(event.target.value)}
                    />
                  </div>
                ))}
              </div>
              {volumeKg != null && (
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  Volume weight {volumeKg} kg
                  {volumeKg > (weightKg || 0)
                    ? ', more than it weighs, so it is charged by volume.'
                    : ', less than it weighs, so it is charged by weight.'}
                </p>
              )}

              <label className="flex items-start gap-2" data-tour="warehouse-dangerous">
                <Checkbox
                  checked={dangerous}
                  onCheckedChange={(checked) => setDangerous(checked === true)}
                />
                <span>
                  <span className="font-medium">Contains dangerous goods</span>
                  <span className="block text-xs text-muted-foreground">
                    Batteries, liquids, powders, magnets, perfume or anything flammable. Charged at
                    the dangerous goods rate.
                  </span>
                </span>
              </label>
              {dangerous && !parcel.declared_dangerous && (
                <p className="flex items-center gap-2 text-xs text-destructive">
                  <TriangleAlert className="h-4 w-4" aria-hidden />
                  The customer said it holds none; admins will be told.
                </p>
              )}

              <div className="space-y-1.5" data-tour="warehouse-ship-week">
                <HintedLabel plain htmlFor="ship-week" hint={FIELD_HELP.shipWeek}>
                  Flies
                </HintedLabel>
                <select
                  id="ship-week"
                  value={shipWeek}
                  onChange={(event) => setShipWeek(event.target.value)}
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  {[...new Set([...(parcel.ship_week ? [parcel.ship_week] : []), ...weeks])].map(
                    (week) => (
                      <option key={week} value={week}>
                        {weekLabel(week)}
                      </option>
                    ),
                  )}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="warehouse-notes">Notes for the team</Label>
                <Textarea
                  id="warehouse-notes"
                  rows={2}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
            </form>
          ) : (
            <p className="text-muted-foreground">This parcel is on a consignment.</p>
          )}

          {error && <p className="text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          {editable && (
            <Button
              type="submit"
              form="process-parcel"
              disabled={!canProcess || busy !== null}
              data-tour="warehouse-process"
            >
              {busy === 'process' && <Loader2 className="h-4 w-4 animate-spin" />}
              {parcel.status === 'RECEIVED' ? 'Save' : 'Mark processed'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RatesCard({
  rates,
  loading,
  isAdmin,
  onChanged,
}: {
  rates: { current: WarehouseRateCard; history: WarehouseRateCard[] } | null
  loading: boolean
  isAdmin: boolean
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [general, setGeneral] = useState('')
  const [dangerous, setDangerous] = useState('')
  const [minimum, setMinimum] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (loading && !rates) return null
  if (!rates) return null
  const { current } = rates

  const start = () => {
    setGeneral(String(current.general_per_kg))
    setDangerous(String(current.dangerous_per_kg))
    setMinimum(String(current.minimum_chargeable_kg))
    setNote('')
    setError(null)
    setEditing(true)
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await apiClient.addWarehouseRates({
        general_per_kg: Number(general),
        dangerous_per_kg: Number(dangerous),
        minimum_kg: Number(minimum),
        note: note.trim() || null,
      })
      setEditing(false)
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not set the rates')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Air rates</CardTitle>
          {isAdmin && !editing && (
            <Button size="sm" variant="outline" onClick={start}>
              Set new rates
            </Button>
          )}
        </div>
        <CardDescription>
          Charged by the greater of actual and volume weight (L × W × H cm ÷ 6000), never under the
          minimum. Enter all three sides to charge by volume. Since{' '}
          {new Date(current.effective_from).toLocaleDateString('en-GB', { dateStyle: 'medium' })}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <dl className="grid grid-cols-3 gap-3">
          <div>
            <dt className="text-xs uppercase text-muted-foreground">General</dt>
            <dd className="mt-1 font-medium">
              {money(current.general_per_kg, current.currency)}/kg
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-muted-foreground">Dangerous goods</dt>
            <dd className="mt-1 font-medium">
              {money(current.dangerous_per_kg, current.currency)}/kg
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-muted-foreground">Minimum</dt>
            <dd className="mt-1 font-medium">{current.minimum_chargeable_kg} kg</dd>
          </div>
        </dl>

        {editing && (
          <form onSubmit={save} className="space-y-3 rounded-md border p-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="rate-general">General $/kg</Label>
                <Input
                  id="rate-general"
                  inputMode="decimal"
                  value={general}
                  onChange={(event) => setGeneral(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rate-dangerous">Dangerous $/kg</Label>
                <Input
                  id="rate-dangerous"
                  inputMode="decimal"
                  value={dangerous}
                  onChange={(event) => setDangerous(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rate-minimum">Minimum kg</Label>
                <Input
                  id="rate-minimum"
                  inputMode="decimal"
                  value={minimum}
                  onChange={(event) => setMinimum(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rate-note">Why (kept with the rates)</Label>
              <Input
                id="rate-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="e.g. Airline fuel surcharge"
              />
            </div>
            {error && <p className="text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Apply from now
              </Button>
            </div>
          </form>
        )}

        {rates.history.some((card) => card.id !== current.id) && (
          <details>
            <summary className="cursor-pointer text-muted-foreground">Earlier rates</summary>
            <ul className="mt-2 space-y-1">
              {rates.history
                .filter((card) => card.id !== current.id)
                .map((card) => (
                  <li key={card.id} className="text-xs text-muted-foreground">
                    {new Date(card.effective_from).toLocaleDateString('en-GB', {
                      dateStyle: 'medium',
                    })}
                    : {money(card.general_per_kg, card.currency)} /{' '}
                    {money(card.dangerous_per_kg, card.currency)} per kg,{' '}
                    {card.minimum_chargeable_kg} kg minimum{card.note ? ` (${card.note})` : ''}
                  </li>
                ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  )
}
